/**
 * How much of the term the workspace is on is left, out of the figure the
 * server reported.
 *
 * The number comes from `billing_state.term.days_left` on
 * `GET /billing/subscription/` — see `trialService`, which maps it — so
 * nothing here subtracts one date from another. That is the whole difference
 * between this and `trialCountdown`, which does: a trial's wire shape carries
 * two instants and the rail has to work the days out against the reader's own
 * clock, while a term's days are counted by the server against the IST
 * calendar access actually ends on. A laptop set to the wrong day would
 * otherwise tell a practice their plan ends tomorrow when it ends next week.
 *
 * Kept apart from `accessLapse`, which words the other side of the same line.
 * This one is about a term that is still running; that one is about a door
 * that has already shut. Exactly one of them ever has anything to say, because
 * `access_source` is `SUBSCRIPTION` right up to the last day of the term and
 * `NONE` the morning after.
 */

/**
 * How near the end counts as near it — the last week.
 *
 * What the dashboard's strip and the foot of the rail draw turns on this. For
 * nearly all of a term the end is months away and saying anything about it
 * would be nagging; inside the last week it is something to act on, and a
 * practice that lets it lapse loses the product on a Tuesday morning with no
 * warning it could have acted on. One number, here rather than in either of
 * the components, so the two cannot disagree about when a term got urgent and
 * draw one without the other.
 *
 * Longer than `TRIAL_URGENT_DAYS`, and deliberately. A trial ends in a
 * fortnight and three days' notice is a fair slice of it; a term runs for
 * months and is a purchase to be arranged, which for a practice that pays by
 * invoice is not a thing done the same afternoon.
 */
export const TERM_URGENT_DAYS = 7;

export interface TermCountdown {
  /** Whole days still to run. `0` is the last day, which is granted whole. */
  daysLeft: number;
  /**
   * What the number says out loud: "in 7 days", "in 1 day", "today".
   *
   * It carries its own preposition because the last day does not take one —
   * "ending in today" is not a sentence. The heading that uses it is written
   * as "Subscription ending " plus this, so the word that changes and the word
   * that has to change with it stay in one place.
   */
  label: string;
}

/**
 * The term's last week, or `null` for every other day of it.
 *
 * `null` is most of the answers and deliberately one value: a term with months
 * to run, a workspace on a trial, one on nothing at all, a read still out, and
 * a backend too old to carry the field. Every screen that words it treats
 * `null` as "say nothing about a term ending", which is true of all five — and
 * that is the right way round, because the failure that matters is telling a
 * practice their plan is about to lapse when it is not.
 *
 * Nothing is said about a term that is already over either. `days_left` is
 * dropped below zero at the boundary, and the morning after the last day the
 * server moves `access_source` to `NONE` — which is `accessLapse`'s to word,
 * and it says the plan has ended rather than that it is ending.
 */
export const termCountdown = (
  daysLeft: number | null | undefined
): TermCountdown | null => {
  if (typeof daysLeft !== "number" || !Number.isFinite(daysLeft)) return null;
  if (daysLeft < 0 || daysLeft > TERM_URGENT_DAYS) return null;

  return {
    daysLeft,
    // "today" rather than "in 0 days", for the reason `trialCountdown` gives:
    // a count of zero is a figure somebody has to convert into a fact, and the
    // fact is what they want. Lower case either way, because it is read as the
    // end of the sentence the heading starts.
    label:
      daysLeft === 0
        ? "today"
        : `in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`,
  };
};

export default termCountdown;
